import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    quote:
      "The high-quality deliverables and timely delivery significantly contributed to our clients' satisfaction. The creative design concepts truly brought our brand to life.",
    name: "Naukri.com",
    role: "Brand Collaterals & Activation",
  },
  {
    quote:
      "Professionalism, efficiency and attention to detail at every step. The high-quality gifts and timely delivery exceeded our expectations — our employees loved them.",
    name: "HCL Technologies",
    role: "Ideapreneurship Activation",
  },
  {
    quote:
      "We gifted 400 employees across 6 cities — every box on time, every card personalised. Effortless and truly premium.",
    name: "Priya Nair",
    role: "People & Culture Lead",
  },
  {
    quote:
      "From ordering to doorstep in two days flat. The champagne dry-fruit hamper was the talk of our entire leadership offsite.",
    name: "Vikram Shah",
    role: "COO, Growth-stage SaaS",
  },
];

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback(
    (d) => {
      setDir(d);
      setIndex((i) => (i + d + testimonials.length) % testimonials.length);
    },
    []
  );

  useEffect(() => {
    const id = setInterval(() => go(1), 6000);
    return () => clearInterval(id);
  }, [go]);

  const t = testimonials[index];

  return (
    <section
      className="relative z-10 overflow-hidden bg-[#0B0F19] py-28 md:py-36"
      data-testid="testimonials-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 bloom-indigo opacity-60" />

      <div className="mx-auto max-w-4xl px-6 text-center md:px-10">
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-sans text-xs uppercase tracking-[0.3em] text-[#D4AF37]"
        >
          Loved by thousands
        </motion.span>

        <div className="relative mt-10 min-h-[280px] md:min-h-[240px]">
          <Quote className="mx-auto mb-6 h-9 w-9 text-[#D4AF37]/40" strokeWidth={1.2} />
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={index}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${index}`}
            >
              <div className="mb-6 flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
                ))}
              </div>
              <p className="mx-auto max-w-3xl font-serif text-2xl italic leading-relaxed tracking-tight text-[#F3EEE3] md:text-3xl">
                “{t.quote}”
              </p>
              <div className="mt-8">
                <p className="font-sans text-sm font-semibold tracking-wide text-[#F3E5AB]">{t.name}</p>
                <p className="mt-1 font-sans text-xs uppercase tracking-[0.15em] text-[#8B93A7]">{t.role}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F3E5AB]/15 text-[#8B93A7] transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDir(i > index ? 1 : -1);
                  setIndex(i);
                }}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? "w-7 bg-[#D4AF37]" : "w-2 bg-[#F3E5AB]/20 hover:bg-[#F3E5AB]/40"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            aria-label="Next testimonial"
            data-testid="testimonial-next"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F3E5AB]/15 text-[#8B93A7] transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
